import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Paper,
} from '@mui/material';
import {
  TrendingUp,
  VideoLibrary,
  Publish,
  Schedule,
  Add,
  Refresh,
  PlayArrow,
  Visibility,
  ThumbUp,
  Comment,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { statsService } from '../services/api';
import { useProjects } from '../contexts/ProjectContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import Paywall from '../components/Paywall/Paywall';
import { usePaywall } from '../hooks/usePaywall';
import UsageMonitor from '../components/UsageMonitor/UsageMonitor';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface DashboardStats {
  total_projects: number;
  total_reels: number;
  total_published: number;
  reels_today: number;
  reels_this_week: number;
  total_views: number;
  total_earnings: number;
  top_projects: any[];
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  trend?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4 }}
  >
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `${color}20`,
              color: color,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={<TrendingUp />}
              label={`${trend > 0 ? '+' : ''}${trend}%`}
              color={trend > 0 ? 'success' : 'error'}
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentProject } = useProjects();
  const { subscription, getRemainingReels } = useSubscription();
  const { paywall, checkReelLimit, closePaywall } = usePaywall();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState<any>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [currentProject]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, trendsRes] = await Promise.all([
        statsService.dashboard(30),
        statsService.trends({
          project_id: currentProject?.id,
          days: 7,
        }),
      ]);
      setStats(statsRes.data);
      setTrendData(trendsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(26, 26, 46, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
      },
    },
  };

  const engagementData = trendData && trendData.engagement && Array.isArray(trendData.engagement) ? {
    labels: trendData.engagement.map((d: any) => d.date),
    datasets: [
      {
        label: 'Views',
        data: trendData.engagement.map((d: any) => d.views || 0),
        borderColor: '#54a0ff',
        backgroundColor: 'rgba(84, 160, 255, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Likes',
        data: trendData.engagement.map((d: any) => d.likes || 0),
        borderColor: '#1dd1a1',
        backgroundColor: 'rgba(29, 209, 161, 0.1)',
        tension: 0.4,
      },
    ],
  } : {
    labels: [],
    datasets: []
  };

  const platformData = stats && stats.top_projects && Array.isArray(stats.top_projects) ? {
    labels: ['Instagram', 'Facebook', 'YouTube', 'TikTok', 'Twitter'],
    datasets: [{
      data: [
        stats.top_projects.reduce((acc, p) => acc + (p.platform_stats && Array.isArray(p.platform_stats) ? (p.platform_stats.find((ps: any) => ps.platform === 'instagram')?.total_posts || 0) : 0), 0),
        stats.top_projects.reduce((acc, p) => acc + (p.platform_stats && Array.isArray(p.platform_stats) ? (p.platform_stats.find((ps: any) => ps.platform === 'facebook')?.total_posts || 0) : 0), 0),
        stats.top_projects.reduce((acc, p) => acc + (p.platform_stats && Array.isArray(p.platform_stats) ? (p.platform_stats.find((ps: any) => ps.platform === 'youtube')?.total_posts || 0) : 0), 0),
        stats.top_projects.reduce((acc, p) => acc + (p.platform_stats && Array.isArray(p.platform_stats) ? (p.platform_stats.find((ps: any) => ps.platform === 'tiktok')?.total_posts || 0) : 0), 0),
        stats.top_projects.reduce((acc, p) => acc + (p.platform_stats && Array.isArray(p.platform_stats) ? (p.platform_stats.find((ps: any) => ps.platform === 'twitter')?.total_posts || 0) : 0), 0),
      ],
      backgroundColor: [
        '#E4405F',
        '#1877F2',
        '#FF0000',
        '#000000',
        '#1DA1F2',
      ],
      borderWidth: 0,
    }],
  } : {
    labels: [],
    datasets: []
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: 4
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's your content overview
          </Typography>
        </Box>
        <Box sx={{
          ml: { xs: 0, sm: 'auto' },
          display: 'flex',
          gap: 2,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchDashboardData}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              if (checkReelLimit()) {
                navigate('/reels/create');
              }
            }}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              flex: { xs: 1, sm: 'initial' }
            }}
          >
            Create Reel
          </Button>
        </Box>
      </Box>

      {/* Usage Monitor Alert */}
      <UsageMonitor variant="compact" />

      {/* Subscription Info */}
      {subscription && (
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)' }}>
          <CardContent>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2
            }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {subscription.plan.name} Plan
                  {subscription.hasByoaAddon && (
                    <Chip label="BYOA" color="secondary" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subscription.plan.reelsLimit === -1
                    ? 'Unlimited reels'
                    : `${getRemainingReels()} of ${subscription.plan.reelsLimit} reels remaining this month`
                  }
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate('/pricing')}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {subscription.plan.type === 'free' ? 'Upgrade' : 'Manage Plan'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Reels"
            value={stats?.total_reels || 0}
            icon={<VideoLibrary />}
            color="#667eea"
            subtitle={`${stats?.reels_today || 0} today`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Published"
            value={stats?.total_published || 0}
            icon={<Publish />}
            color="#1dd1a1"
            subtitle={`${stats?.reels_this_week || 0} this week`}
            trend={15}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Views"
            value={stats?.total_views?.toLocaleString() || 0}
            icon={<Visibility />}
            color="#54a0ff"
            trend={32}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Earnings"
            value={`$${stats?.total_earnings?.toFixed(2) || '0.00'}`}
            icon={<TrendingUp />}
            color="#feca57"
            trend={8}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: { xs: 300, sm: 350, md: 400 } }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Engagement Trends
              </Typography>
              {engagementData ? (
                <Box sx={{ height: 'calc(100% - 40px)' }}>
                  <Line data={engagementData} options={chartOptions} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: { xs: 300, sm: 350, md: 400 } }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Platform Distribution
              </Typography>
              {platformData ? (
                <Box sx={{ height: 'calc(100% - 40px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ width: '80%', height: '80%' }}>
                    <Doughnut data={platformData} options={{ ...chartOptions, maintainAspectRatio: true }} />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Projects */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Top Performing Projects</Typography>
            <Button
              size="small"
              sx={{ ml: 'auto' }}
              onClick={() => navigate('/analytics')}
            >
              View All Analytics
            </Button>
          </Box>
          {stats?.top_projects && Array.isArray(stats.top_projects) ? stats.top_projects.map((project, index) => (
            <Paper
              key={project.project_id}
              sx={{
                p: 2,
                mb: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.05)',
                },
              }}
              onClick={() => navigate(`/projects/${project.project_id}`)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {project.project_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <VideoLibrary sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {project.total_reels} reels
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Publish sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {project.published_reels} published
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {Math.round(project.average_render_time)}s avg render
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  {project.platform_stats && Array.isArray(project.platform_stats) ? project.platform_stats.map((platform: any) => (
                    <Box key={platform.platform} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip
                        size="small"
                        label={platform.platform}
                        className={`platform-${platform.platform}`}
                      />
                      <Typography variant="caption">
                        {platform.total_views?.toLocaleString() || '0'} views
                      </Typography>
                    </Box>
                  )) : (
                    <Typography variant="caption" color="text.secondary">
                      No platform data
                    </Typography>
                  )}
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(project.published_reels / project.total_reels) * 100}
                sx={{ mt: 2, height: 6, borderRadius: 3 }}
              />
            </Paper>
          )) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No projects available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Paywall Dialog */}
      {paywall && (
        <Paywall
          open={true}
          onClose={closePaywall}
          feature={paywall.feature}
          description={paywall.description}
          requiredPlan={paywall.requiredPlan}
        />
      )}
    </Box>
  );
};

export default Dashboard;